import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import apiService from '../services/api';
import { AssistantMessage, AssistantMode, AssistantPart } from '../types/assistant';
import { autopilotRegistry } from '../autopilot/registry';

/** Lightweight summary for the history list (no message bodies). */
export interface ConversationSummary {
  id: string;
  title: string;
  updatedAt: number;
  messageCount: number;
}

/** A full saved conversation. */
interface StoredConversation {
  id: string; // local record id
  conversationId: string | null; // CA conversation id — resumes agent memory
  title: string;
  messages: AssistantMessage[];
  createdAt: number;
  updatedAt: number;
}

interface AssistantContextType {
  open: boolean;
  messages: AssistantMessage[];
  sending: boolean;
  /** past + current chats, newest first */
  conversations: ConversationSummary[];
  /** id of the open chat, or null for a fresh (unsaved) chat */
  activeId: string | null;
  /** 'ask' = read-only Q&A; 'autopilot' = the agent controls the active page */
  mode: AssistantMode;
  setMode: (mode: AssistantMode) => void;
  openAssistant: () => void;
  closeAssistant: () => void;
  toggleAssistant: () => void;
  sendMessage: (question: string) => Promise<void>;
  /** resolve a gated auto-pilot action (e.g. save rankings) */
  resolveConfirm: (messageId: string, partIndex: number, approve: boolean) => Promise<void>;
  /** archive the current chat and start a fresh one (does NOT delete anything) */
  newConversation: () => void;
  openConversation: (id: string) => void;
  deleteConversation: (id: string) => void;
  /** kept for back-compat; same as newConversation */
  clearConversation: () => void;
}

const AssistantContext = createContext<AssistantContextType | undefined>(undefined);

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const EMPTY: AssistantMessage[] = [];

const STORAGE_KEY = 'squadlab_assistant_v2';
const LEGACY_KEY = 'squadlab_assistant_v1';
const MAX_CONVERSATIONS = 30;
const MAX_MESSAGES = 100;

const titleFrom = (messages: AssistantMessage[]): string => {
  const firstUser = messages.find((m) => m.role === 'user');
  const t = firstUser?.parts.find((p) => p.type === 'text');
  const text = t && t.type === 'text' ? t.text.trim() : '';
  if (!text) return 'New chat';
  return text.length > 48 ? text.slice(0, 47) + '…' : text;
};

interface PersistShape {
  activeId: string | null;
  conversations: StoredConversation[];
  mode?: AssistantMode;
}

const loadPersisted = (): PersistShape => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const stored: StoredConversation[] = Array.isArray(parsed?.conversations) ? parsed.conversations : [];
      const conversations = stored
        .map((c) => ({ ...c, messages: (c.messages || []).filter((m) => m && !m.pending && Array.isArray(m.parts)) }))
        .filter((c) => c.id && c.messages.length > 0);
      const activeId =
        typeof parsed?.activeId === 'string' && conversations.some((c) => c.id === parsed.activeId)
          ? parsed.activeId
          : null;
      const mode: AssistantMode = parsed?.mode === 'autopilot' ? 'autopilot' : 'ask';
      return { activeId, conversations, mode };
    }
    // One-time migration from the old single-thread shape.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const p = JSON.parse(legacy);
      const msgs: AssistantMessage[] = Array.isArray(p?.messages)
        ? p.messages.filter((m: AssistantMessage) => m && !m.pending && Array.isArray(m.parts))
        : [];
      if (msgs.length) {
        const id = newId();
        const now = Date.now();
        const conv: StoredConversation = {
          id,
          conversationId: typeof p?.conversationId === 'string' ? p.conversationId : null,
          title: titleFrom(msgs),
          messages: msgs,
          createdAt: now,
          updatedAt: now,
        };
        return { activeId: id, conversations: [conv] };
      }
    }
  } catch {
    /* ignore */
  }
  return { activeId: null, conversations: [] };
};

const summarize = (c: StoredConversation): ConversationSummary => ({
  id: c.id,
  title: c.title,
  updatedAt: c.updatedAt,
  messageCount: c.messages.length,
});

/**
 * Auto-pilot: execute the agent's ui_action parts on the active page, in order,
 * with a short beat between them so the user can watch the page being driven.
 * Heavy `args` payloads are stripped before the message is stored.
 */
const runUiActions = async (parts: AssistantPart[]): Promise<AssistantPart[]> => {
  const out: AssistantPart[] = [];
  for (const part of parts) {
    if (part.type === 'ui_action') {
      let status: 'done' | 'failed' = 'done';
      try {
        await autopilotRegistry.execute(part.action, part.args);
        await new Promise((r) => setTimeout(r, 350));
      } catch (e) {
        console.error('Auto-pilot action failed:', part.action, e);
        status = 'failed';
      }
      out.push({ type: 'ui_action', action: part.action, label: part.label, status });
    } else {
      out.push(part);
    }
  }
  return out;
};

export const AssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initial = useRef<PersistShape>(loadPersisted());
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [conversations, setConversations] = useState<StoredConversation[]>(initial.current.conversations);
  const [activeId, setActiveId] = useState<string | null>(initial.current.activeId);
  const [mode, setMode] = useState<AssistantMode>(initial.current.mode || 'ask');

  // Latest values for the async sendMessage without re-creating it each turn.
  const convosRef = useRef(conversations);
  const activeIdRef = useRef(activeId);
  useEffect(() => {
    convosRef.current = conversations;
  }, [conversations]);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);
  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  // Persist every chat (drop in-flight messages, cap sizes/counts).
  useEffect(() => {
    try {
      const toStore = conversations
        .slice()
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .slice(0, MAX_CONVERSATIONS)
        .map((c) => ({ ...c, messages: c.messages.filter((m) => !m.pending).slice(-MAX_MESSAGES) }))
        .filter((c) => c.messages.length > 0);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ activeId, conversations: toStore, mode }));
    } catch {
      /* storage full / unavailable — non-fatal */
    }
  }, [conversations, activeId, mode]);

  const active = conversations.find((c) => c.id === activeId) || null;
  const messages = active?.messages ?? EMPTY;

  const openAssistant = useCallback(() => setOpen(true), []);
  const closeAssistant = useCallback(() => setOpen(false), []);
  const toggleAssistant = useCallback(() => setOpen((o) => !o), []);

  // Start a fresh chat. The current one stays saved in `conversations`.
  const newConversation = useCallback(() => setActiveId(null), []);
  const openConversation = useCallback((id: string) => setActiveId(id), []);
  const deleteConversation = useCallback((id: string) => {
    setConversations((prev) => prev.filter((c) => c.id !== id));
    setActiveId((cur) => (cur === id ? null : cur));
  }, []);

  const patchConvo = useCallback(
    (id: string, patch: (c: StoredConversation) => Partial<StoredConversation>) => {
      setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch(c), updatedAt: Date.now() } : c)));
    },
    []
  );

  const sendMessage = useCallback(
    async (question: string) => {
      const text = question.trim();
      if (!text || sending) return;

      const userMsg: AssistantMessage = {
        id: newId(),
        role: 'user',
        parts: [{ type: 'text', text }],
        createdAt: Date.now(),
      };
      const pendingId = newId();
      const pendingMsg: AssistantMessage = {
        id: pendingId,
        role: 'assistant',
        parts: [],
        createdAt: Date.now(),
        pending: true,
      };

      // Resolve the active chat, or create one for a fresh session.
      let convoId = activeIdRef.current;
      let priorMessages: AssistantMessage[] = [];
      let caConversationId: string | null = null;

      if (convoId) {
        const c = convosRef.current.find((x) => x.id === convoId);
        priorMessages = c?.messages ?? [];
        caConversationId = c?.conversationId ?? null;
        patchConvo(convoId, (cc) => ({ messages: [...cc.messages, userMsg, pendingMsg] }));
      } else {
        convoId = newId();
        const now = Date.now();
        const created: StoredConversation = {
          id: convoId,
          conversationId: null,
          title: titleFrom([userMsg]),
          messages: [userMsg, pendingMsg],
          createdAt: now,
          updatedAt: now,
        };
        setConversations((prev) => [created, ...prev]);
        setActiveId(convoId);
        activeIdRef.current = convoId;
      }

      const history = priorMessages
        .map((m) => {
          const firstText = m.parts.find((p) => p.type === 'text');
          return firstText && firstText.type === 'text' ? { role: m.role, text: firstText.text } : null;
        })
        .filter((h): h is { role: 'user' | 'assistant'; text: string } => h !== null);

      setSending(true);
      try {
        if (modeRef.current === 'autopilot') {
          // ---- Auto-pilot: page-aware, page-controlling agent ----
          const reg = autopilotRegistry.getActive();
          if (!reg) {
            patchConvo(convoId, (c) => ({
              messages: c.messages.map((m) =>
                m.id === pendingId
                  ? {
                      ...m,
                      pending: false,
                      parts: [
                        {
                          type: 'text' as const,
                          text:
                            'Auto-pilot currently controls the **Lineups** and **Trade Machine** ' +
                            'pages. Open one of them and try again, or switch back to Ask mode.',
                        },
                      ],
                    }
                  : m
              ),
            }));
            return;
          }
          const resp = await apiService.askAutopilot({
            question: text,
            page: { id: reg.page, path: window.location.pathname, state: reg.describeState() },
            history,
          });
          // Execute the agent's actions on the page, then store the slimmed parts.
          const processed = await runUiActions(resp.message.parts);
          patchConvo(convoId, (c) => ({
            messages: c.messages.map((m) =>
              m.id === pendingId ? { ...m, pending: false, parts: processed } : m
            ),
          }));
          return;
        }

        const resp = await apiService.askAssistant({ question: text, history, conversationId: caConversationId });
        patchConvo(convoId, (c) => ({
          conversationId: resp.conversationId || c.conversationId,
          messages: c.messages.map((m) => (m.id === pendingId ? { ...m, pending: false, parts: resp.message.parts } : m)),
        }));
      } catch (e) {
        patchConvo(convoId, (c) => ({
          messages: c.messages.map((m) =>
            m.id === pendingId
              ? {
                  ...m,
                  pending: false,
                  error: true,
                  parts: [
                    { type: 'text', text: "Sorry — I couldn't reach the projections service. Please try again." },
                  ],
                }
              : m
          ),
        }));
      } finally {
        setSending(false);
      }
    },
    [sending, patchConvo]
  );

  /**
   * Resolve a gated auto-pilot action (confirm part). On approval the action is
   * executed on the active page (e.g. the real save), and the outcome is
   * appended to the thread. Either way the part is marked resolved so the
   * buttons can't fire twice.
   */
  const resolveConfirm = useCallback(
    async (messageId: string, partIndex: number, approve: boolean) => {
      const convo = convosRef.current.find((c) => c.messages.some((m) => m.id === messageId));
      if (!convo) return;
      const msg = convo.messages.find((m) => m.id === messageId);
      const part = msg?.parts[partIndex];
      if (!part || part.type !== 'confirm' || part.resolved) return;

      const mark = (resolved: 'confirmed' | 'cancelled') =>
        patchConvo(convo.id, (c) => ({
          messages: c.messages.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  parts: m.parts.map((p, i) =>
                    i === partIndex && p.type === 'confirm' ? { ...p, resolved } : p
                  ),
                }
              : m
          ),
        }));

      if (!approve) {
        mark('cancelled');
        return;
      }
      mark('confirmed');
      let resultText: string;
      try {
        const note = await autopilotRegistry.execute(part.action, part.args);
        resultText = typeof note === 'string' && note ? note : '✅ Done — the action completed.';
      } catch (e: any) {
        resultText = `❌ Couldn't complete the action: ${e?.message || 'unknown error'}`;
      }
      const doneMsg: AssistantMessage = {
        id: newId(),
        role: 'assistant',
        parts: [{ type: 'text', text: resultText }],
        createdAt: Date.now(),
      };
      patchConvo(convo.id, (c) => ({ messages: [...c.messages, doneMsg] }));
    },
    [patchConvo]
  );

  const summaries = conversations
    .slice()
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .map(summarize);

  return (
    <AssistantContext.Provider
      value={{
        open,
        messages,
        sending,
        conversations: summaries,
        activeId,
        mode,
        setMode,
        openAssistant,
        closeAssistant,
        toggleAssistant,
        sendMessage,
        resolveConfirm,
        newConversation,
        openConversation,
        deleteConversation,
        clearConversation: newConversation,
      }}
    >
      {children}
    </AssistantContext.Provider>
  );
};

export const useAssistant = (): AssistantContextType => {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used within an AssistantProvider');
  return ctx;
};
